import {
  getHexacoApiAppCode,
  getHexacoApiBaseUrl,
} from "../constants/hexacoApiEnv";
import { normalizeSiteLanguage, SiteLanguage } from "../i18n/language";

const SESSION_ORDER_ID_KEY = "hexacoWebOrderId";
const SESSION_ACCESS_TOKEN_KEY = "hexacoWebAccessToken";
const SESSION_CUSTOMER_NAME_KEY = "hexacoWebCustomerName";

export type HexacoWebOrderStatus =
  | "CREATED"
  | "TNC_ACCEPTED"
  | "PDF_UPLOADED"
  | "EXTRACTING_SCORES"
  | "SCORES_EXTRACTED"
  | "ANALYZING"
  | "GENERATING_PDF"
  | "REPORT_READY"
  | "CHECKOUT_PENDING"
  | "PAID"
  | "FAILED"
  | "EXPIRED"
  | "REFUNDED";

export interface HexacoWebCreateOrderResponse {
  orderId: string;
  accessToken: string;
  status: HexacoWebOrderStatus;
}

export interface HexacoWebUploadResponse {
  status: HexacoWebOrderStatus;
}

export interface HexacoWebStatusResponse {
  status: HexacoWebOrderStatus;
  failureStage?: string | null;
  failureMessage?: string | null;
}

export interface HexacoWebCheckoutResponse {
  checkoutUrl: string;
}

export interface HexacoWebDownloadResponse {
  downloadUrl: string;
}

type ApiEnvelope<T> = {
  status?: string;
  statusCode?: number;
  data?: T;
  error?: {
    code?: string;
    message?: string;
    details?: string;
    path?: string;
  };
  requestId?: string;
};

type HexacoWebResult<T> = {
  success: boolean;
  status: string;
  data: T | null;
  error: { message?: string } | null;
};

export class HexacoWebApiError extends Error {
  readonly status: number;
  readonly retryAfter?: number;
  /** HEXACO business status when HTTP 200 but `success === false` (e.g. `NOT_HEXACO_PDF`). */
  readonly businessStatus?: string;

  constructor(
    message: string,
    status: number,
    options?: { retryAfter?: number; businessStatus?: string },
  ) {
    super(message);
    this.name = "HexacoWebApiError";
    this.status = status;
    this.retryAfter = options?.retryAfter;
    this.businessStatus = options?.businessStatus;
  }
}

export const mapSiteLanguageToApiLanguage = (
  language?: string | null,
): string => {
  const siteLanguage = normalizeSiteLanguage(language);
  if (siteLanguage === "en") return "en";
  if (siteLanguage === "zh-CN") return "zh_hans";
  return "zh_hk";
};

export const saveHexacoWebSession = (
  orderId: string,
  accessToken: string,
  customerName?: string,
): void => {
  sessionStorage.setItem(SESSION_ORDER_ID_KEY, orderId);
  sessionStorage.setItem(SESSION_ACCESS_TOKEN_KEY, accessToken);
  const trimmedName = customerName?.trim();
  if (trimmedName) {
    sessionStorage.setItem(SESSION_CUSTOMER_NAME_KEY, trimmedName);
  } else {
    sessionStorage.removeItem(SESSION_CUSTOMER_NAME_KEY);
  }
};

export const getHexacoWebSession = (): {
  orderId: string | null;
  accessToken: string | null;
  customerName: string | null;
} => ({
  orderId: sessionStorage.getItem(SESSION_ORDER_ID_KEY),
  accessToken: sessionStorage.getItem(SESSION_ACCESS_TOKEN_KEY),
  customerName: sessionStorage.getItem(SESSION_CUSTOMER_NAME_KEY),
});

export const clearHexacoWebSession = (): void => {
  sessionStorage.removeItem(SESSION_ORDER_ID_KEY);
  sessionStorage.removeItem(SESSION_ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_CUSTOMER_NAME_KEY);
};

const parseRetryAfter = (response: Response): number | undefined => {
  const header = response.headers.get("Retry-After");
  if (!header) return undefined;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) ? seconds : undefined;
};

const pickString = (
  payload: Record<string, unknown>,
  keys: string[],
): string | undefined => {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }
  return undefined;
};

const parseEnvelopeErrorMessage = (
  envelope: ApiEnvelope<unknown>,
  httpStatus: number,
): string => {
  const envelopeError = envelope.error;
  if (envelopeError && typeof envelopeError === "object") {
    if (
      typeof envelopeError.details === "string" &&
      envelopeError.details.length > 0
    ) {
      return envelopeError.details;
    }
    if (
      typeof envelopeError.message === "string" &&
      envelopeError.message.length > 0
    ) {
      return envelopeError.message;
    }
  }

  return `Request failed (${httpStatus})`;
};

const parseHexacoBusinessErrorMessage = (
  hexaco: HexacoWebResult<unknown>,
): string => {
  const wrapperMessage = hexaco.error?.message;
  if (typeof wrapperMessage === "string" && wrapperMessage.length > 0) {
    return wrapperMessage;
  }
  return "Request failed";
};

async function parseJsonResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new HexacoWebApiError(
      text || "Unexpected server response",
      response.status,
    );
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function parseHexacoWrapper(
  envelope: ApiEnvelope<unknown>,
): HexacoWebResult<Record<string, unknown>> | null {
  const wrapperRecord = asRecord(envelope.data);
  if (!wrapperRecord) {
    return null;
  }

  const innerData = wrapperRecord.data;
  return {
    success: wrapperRecord.success === true,
    status: typeof wrapperRecord.status === "string" ? wrapperRecord.status : "",
    data: asRecord(innerData),
    error:
      wrapperRecord.error && typeof wrapperRecord.error === "object"
        ? (wrapperRecord.error as { message?: string })
        : null,
  };
}

async function requestHexaco<T extends Record<string, unknown>>(
  path: string,
  init: RequestInit = {},
  accessToken?: string,
  options?: { allowBusinessFailure?: boolean },
): Promise<T> {
  const headers = new Headers(init.headers || {});

  if (accessToken) {
    headers.set("X-Hexaco-Order-Token", accessToken);
  }

  if (
    init.body &&
    !(init.body instanceof FormData) &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }

  const appCode = getHexacoApiAppCode();
  if (appCode) {
    headers.set("Authorization", `APPCODE ${appCode}`);
  }

  const response = await fetch(`${getHexacoApiBaseUrl()}${path}`, {
    ...init,
    headers,
  });

  const raw = await parseJsonResponse(response);
  const envelope = (asRecord(raw) || {}) as ApiEnvelope<unknown>;

  if (!response.ok) {
    throw new HexacoWebApiError(
      parseEnvelopeErrorMessage(envelope, response.status),
      response.status,
      { retryAfter: parseRetryAfter(response) },
    );
  }

  const hexaco = parseHexacoWrapper(envelope);
  if (!hexaco) {
    throw new HexacoWebApiError("Invalid API response", response.status);
  }

  if (!hexaco.success && !options?.allowBusinessFailure) {
    throw new HexacoWebApiError(parseHexacoBusinessErrorMessage(hexaco), 200, {
      businessStatus: hexaco.status || undefined,
    });
  }

  if (!hexaco.data) {
    throw new HexacoWebApiError("Invalid API response payload", response.status, {
      businessStatus: hexaco.status || undefined,
    });
  }

  return hexaco.data as T;
}

export async function createOrder(options: {
  language: SiteLanguage | string;
  tncAccepted: boolean;
  customerEmail: string;
  customerName?: string;
}): Promise<HexacoWebCreateOrderResponse> {
  const payload = await requestHexaco<Record<string, unknown>>(
    "/hexaco-web/orders",
    {
      method: "POST",
      body: JSON.stringify({
        language: mapSiteLanguageToApiLanguage(options.language),
        tncAccepted: options.tncAccepted,
        customerEmail: options.customerEmail,
        ...(options.customerName
          ? { customerName: options.customerName }
          : {}),
      }),
    },
  );

  const orderId = pickString(payload, ["orderId", "order_id"]);
  const accessToken = pickString(payload, ["accessToken", "access_token"]);
  const status = pickString(payload, ["status"]) as HexacoWebOrderStatus | undefined;

  if (!orderId || !accessToken || !status) {
    throw new HexacoWebApiError("Invalid create order response", 500);
  }

  saveHexacoWebSession(orderId, accessToken, options.customerName);

  return { orderId, accessToken, status };
}

export async function uploadPdf(
  orderId: string,
  token: string,
  file: File,
  turnstileToken: string,
): Promise<HexacoWebUploadResponse> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("turnstileToken", turnstileToken);

  const payload = await requestHexaco<Record<string, unknown>>(
    `/hexaco-web/orders/${orderId}/upload`,
    {
      method: "POST",
      body: formData,
    },
    token,
  );

  const status = pickString(payload, ["status"]) as HexacoWebOrderStatus | undefined;
  if (!status) {
    throw new HexacoWebApiError("Invalid upload response", 500);
  }

  return { status };
}

export async function createCheckout(
  orderId: string,
  token: string,
): Promise<HexacoWebCheckoutResponse> {
  const payload = await requestHexaco<Record<string, unknown>>(
    `/hexaco-web/orders/${orderId}/checkout`,
    { method: "POST" },
    token,
  );

  const checkoutUrl = pickString(payload, ["checkoutUrl", "checkout_url", "url"]);
  if (!checkoutUrl) {
    throw new HexacoWebApiError("Invalid checkout response", 500);
  }

  return { checkoutUrl };
}

export async function getOrderStatus(
  orderId: string,
  token: string,
): Promise<HexacoWebStatusResponse> {
  const payload = await requestHexaco<Record<string, unknown>>(
    `/hexaco-web/orders/${orderId}/status`,
    { method: "GET" },
    token,
    { allowBusinessFailure: true },
  );

  const status = pickString(payload, ["status"]) as HexacoWebOrderStatus | undefined;
  if (!status) {
    throw new HexacoWebApiError("Invalid status response", 500);
  }

  return {
    status,
    failureStage: pickString(payload, ["failureStage", "failure_stage"]) || null,
    failureMessage:
      pickString(payload, ["failureMessage", "failure_message"]) || null,
  };
}

export async function getDownloadUrl(
  orderId: string,
  token: string,
): Promise<HexacoWebDownloadResponse> {
  const payload = await requestHexaco<Record<string, unknown>>(
    `/hexaco-web/orders/${orderId}/download`,
    { method: "GET" },
    token,
  );

  const downloadUrl = pickString(payload, ["downloadUrl", "download_url", "url"]);
  if (!downloadUrl) {
    throw new HexacoWebApiError("Invalid download response", 500);
  }

  return { downloadUrl };
}
